import React from "react";

import { APP_URLS } from "./appConstants";

export interface KPIRowData {
    relatedSOP: {
        name: string;
        link: string;
    };
    currentProgress: string;
    processPoint: string;
    plannedMeasurement: string;
    reviewFrequency: string;
    performedBy: string;
    performedIn: string;
    documentation: JSX.Element;
    kpi: string;
    improvementGoals: string;
    trendingAnalysisMethodology: string;
}

export const KPI_TABLE_HEADER = [
    "Related SOP",
    "Current Progress",
    "Process point",
    "Planned measurement",
    "Review Frequency",
    "Performed by (WHO)",
    "Performed in (WHEN",
    "Documentation (HOW to measure and document?)",
    "Key Performance Indicator (KPI)",
    "Improvement goals",
    "Trending Analysis methodology",
    "Action",
];

export const SOP_REVIEW_PROCESS_POINT = "SOP Review";
export const CHANGE_REQUEST_CLOSURE_PROCESS_POINT = "Change Request Closure";
export const SUPPLIER_PERFORMANCE_PROCESS_POINT = "Supplier Performance";
export const SUPPLIER_RE_EVALUATION_PROCESS_POINT = "Supplier Re-Evaluation";
export const PURCHASE_ACCEPTANCE_PROCESS_POINT = "Purchase Acceptance";
export const RESIDUAL_RISK_PROCESS_POINT = "Residual Risk";
export const TRAINING_COMPETENCY_PROCESS_POINT = "Training competency";
export const TRAINING_MANAGEMENT_PROCESS_POINT = "Training Management";
export const CAPA_MANAGEMENT_PROCESS_POINT = "CAPA Management";
export const CAPA_EFFECTIVENESS_PROCESS_POINT = "CAPA Effectiveness";
export const NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT = "Nonconformance & investigation";
export const INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT = "Internal audit frequency";
export const MANAGEMENT_REVIEW_PROCESS_POINT = "Management review";
export const NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT = "Number of created vs resolved issues";
export const MEASUREMENT_DEVICE_PROCESS_POINT = "Measurement Device";
export const CONTROL_OF_NON_CONFORMING_PRODUCTS_PROCESS_POINT = "Control of Non-Conforming Products";
export const SCHEDULE_FOR_RECURRING_QMS_ACTIVITIES_PROCESS_POINT = "Schedule for Recurring QMS Activities";
export const DEVICE_MANAGEMENT_PROCESS_POINT = "Device Management";

const getDocumentationForSOPReview = () => {
    return (
        <>
            <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/1792049239"> L4-LOG-002 SOP Document List</a>
            <p>(Compare "Date of Approval" against "Review Due Date")</p>
        </>
    );
};

const getDefaultDataForSOPReview = (): KPIRowData => {
    return {
        relatedSOP: {
            name: "L2-SP-001 Document Control SOP",
            link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1238892550/L2-SP-001+Document+Control+SOP",
        },
        currentProgress: "100% of Class B & C suppliers scored by the end of the calendar year.",
        processPoint: "SOP Review",
        plannedMeasurement: "Timely completion of reviews",
        reviewFrequency: "Semi-annual",
        performedBy: "QA",
        performedIn: "Management Review",
        kpi: "100% SOPs are reviewed within 2 years from the last review",
        improvementGoals: "N/A",
        trendingAnalysisMethodology: "Time series statistics",
        documentation: (
            <>
                <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/1792049239"> L4-LOG-002 SOP Document List</a>
                <p>(Compare "Date of Approval" against "Review Due Date")</p>
            </>
        ),
    };
};

const getDefeaultDataForChangeRequestClosure = (): KPIRowData => {
    return {
        relatedSOP: {
            name: "L2-SP-038 Change Control",
            link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/2133098603",
        },
        currentProgress: "100%  SOPs are reviewed within 2 years from the last review",
        processPoint: "Change request closure",
        plannedMeasurement: "Time series statistics",
        reviewFrequency: "Quarterly",
        performedBy: "QA",
        performedIn: "Management Review",
        documentation: (
            <>
                <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/1894809951">L4-LOG-016 L4-ECR</a>
                <p>(compare "closure date" vs "initiation date")</p>
            </>
        ),
        kpi: "≥90% of Change Requests shall be closed within the designated timeframe.",
        improvementGoals: "N/A",
        trendingAnalysisMethodology: "Time series statistics",
    };
};

export interface KPI_MAP {
    reviewProcessPoint: string;
    data: KPIRowData;
    pageUrl?: string;
}

export const KPI_LIST: KPI_MAP[] = [
    {
        reviewProcessPoint: SOP_REVIEW_PROCESS_POINT,
        data: getDefaultDataForSOPReview(),
        pageUrl: APP_URLS.SOP_REVIEW,
    },
    {
        reviewProcessPoint: CHANGE_REQUEST_CLOSURE_PROCESS_POINT,
        data: getDefeaultDataForChangeRequestClosure(),
    },
    {
        reviewProcessPoint: SUPPLIER_PERFORMANCE_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-005 Supplier Control",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1247903925/L2-SP-005+Supplier+Control",
            },
            currentProgress: "N/A",
            processPoint: "Supplier Performance",
            plannedMeasurement: "Timely completion of supplier Performance score",
            reviewFrequency: "Annual",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: (
                <>
                    Data collected from
                    <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/1890156672"> L4-LOG-015 Supplier Monitoring Sheet</a>
                </>
            ),
            kpi: "100% of Class B & C suppliers scored by the end of the calendar year.",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: SUPPLIER_RE_EVALUATION_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-005 Supplier Control",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1247903925/L2-SP-005+Supplier+Control",
            },
            currentProgress: "N/A",
            processPoint: "Supplier Re-Evaluation",
            plannedMeasurement: "Timely completion of supplier Re-Evaluation",
            reviewFrequency: "Annual",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Data collected from Supplier Re-Evaluation</>,
            kpi: "100% Supplier Re-evaluation for Class B & C Suppliers by the end of the calendar year.",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: PURCHASE_ACCEPTANCE_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-005 Supplier Control",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1247903925/L2-SP-005+Supplier+Control",
            },
            currentProgress: "N/A",
            processPoint: "Purchase Acceptance",
            plannedMeasurement: "Timely completion of Goods Receipt Notes",
            reviewFrequency: "Quarterly",
            performedBy: "All functions",
            performedIn: "Compare “Delivery date” vs “Date made effective” on GRN records",
            documentation: <>Data collected from Purchase Acceptance</>,
            kpi: "≥90% of GRNs are made effective within the designated timeframe",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.PURCHASE_ACCEPTANCE,
    },
    {
        reviewProcessPoint: RESIDUAL_RISK_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-006 Risk Management",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1247969353/L2-SP-006+Risk+Management",
            },
            currentProgress: "N/A",
            processPoint: "Residual Risk",
            plannedMeasurement: "Maintaining Acceptable Risk Levels",
            reviewFrequency: "Quarterly",
            performedBy: "All functions",
            performedIn: "Management Review",
            documentation: <>Quarterly Risk Report</>,
            kpi: "≥95% of risk is reduced to acceptable level or risk-benefit performed on unacceptable risks",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: TRAINING_COMPETENCY_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-007 Human Resource & Training",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1248035089",
            },
            currentProgress: "N/A",
            processPoint: "Training competency",
            plannedMeasurement: "Quiz score",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Training records</>,
            kpi: "100% training completion with demonstrated comprehension.",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.TRAINING_COMPETENCY,
    },
    {
        reviewProcessPoint: TRAINING_MANAGEMENT_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-007 Human Resource & Training",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1248035089",
            },
            currentProgress: "N/A",
            processPoint: "Training Management",
            plannedMeasurement: "Timely Closure of Training Reports",
            reviewFrequency: "Quarterly QA Managem ent Review",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Compare “effective date” of document to be trained on against “effective date” of corresponding training records</>,
            kpi: "90% of Training Records are made effective within the designated timeframe",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.TRAINING_MANAGEMENT,
    },
    {
        reviewProcessPoint: CAPA_MANAGEMENT_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-008 Nonconformance, Investigation, and Corrective and Preventive Actions (CAPA)",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1248100625/L2-SP-008+Nonconformance+Investigation+and+Corrective+and+Preventive+Actions+CAPA",
            },
            currentProgress: "N/A",
            processPoint: "CAPA Management",
            plannedMeasurement: "Timely Closure of Corrective Actions (CAPAs)",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management review",
            documentation: <>Compare “effective date” of nonconformance report against “effective date” of corresponding CAPA for closure</>,
            kpi: "≥90% of CAPA cases are closed within the designated timeframe",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.CAPA_MANAGEMENT,
    },
    {
        reviewProcessPoint: CAPA_EFFECTIVENESS_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-008 Nonconformance, Investigation, and Corrective and Preventive Actions (CAPA)",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1250164737",
            },
            currentProgress: "N/A",
            processPoint: "CAPA Effectiveness",
            plannedMeasurement: "CAPA Effectiveness",
            reviewFrequency: "Semi-Annually",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>CAPA meets the effectiveness check expectations.</>,
            kpi: "≥90% of CAPAs are considered effective",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-008 Nonconformance, Investigation, and Corrective and Preventive Actions (CAPA)",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1248100625/L2-SP-008+Nonconformance+Investigation+and+Corrective+and+Preventive+Actions+CAPA",
            },
            currentProgress: "N/A",
            processPoint: "Nonconformance & investigation",
            plannedMeasurement: "Timely Closure of Nonconformance and Investigations",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Compare nonconformance record “date discovered” against “effective date”</>,
            kpi: "100% of Nonconformance Reports are made effective within the designated timeframe",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.NONCONFORMANCE_AND_INVESTIGATION,
    },
    {
        reviewProcessPoint: INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-011 Internal Audit",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1250164773",
            },
            currentProgress: "N/A",
            processPoint: "Internal audit frequency",
            plannedMeasurement: "On-time internal audit",
            reviewFrequency: "Annually",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Tracking by looking at Internal audit report</>,
            kpi: "Internal audit is conducted at least once a year",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.INTERNAL_AUDIT,
    },
    {
        reviewProcessPoint: MANAGEMENT_REVIEW_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-013 Management Responsibility ",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1250459690",
            },
            currentProgress: "N/A",
            processPoint: "Management review",
            plannedMeasurement: "On-time Management review",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: (
                <>
                    Tracking by looking at Management Review Meeting Minute
                    <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/2203287602"> L4-LOG-025 Management Review (L4-MR)</a>
                </>
            ),
            kpi: "Management Review is conducted at least once a year",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.MANAGEMENT_REVIEWS,
    },
    {
        reviewProcessPoint: NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-020 Product Development and Design Control",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1251409948",
            },
            currentProgress: "N/A",
            processPoint: "Number of created vs resolved issues",
            plannedMeasurement: "Issues completion percentage",
            reviewFrequency: "Quarterly",
            performedBy: "Project Manager",
            performedIn: "Management Review",
            documentation: <>Report generated by Jira</>,
            kpi: "Number of created vs resolved (“Demo review” onwards) issues >80%",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.NUMBER_OF_CREATED_AND_RESOLVED,
    },
    {
        reviewProcessPoint: MEASUREMENT_DEVICE_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-021 Control of Measuring and Monitoring Devices ",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1251377179",
            },
            currentProgress: "N/A",
            processPoint: "Measurement Device",
            plannedMeasurement: "Regular Conduct of Inspections",
            reviewFrequency: "Semi-Annually",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: (
                <>
                    Refer to
                    <a href="https://pascallqms.atlassian.net/wiki/spaces/QMSFIL/pages/2075230209"> L4-LOG-020 Equipment List </a>
                </>
            ),
            kpi: "Maintain compliance with scheduled calibration intervals for measurement devices",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: CONTROL_OF_NON_CONFORMING_PRODUCTS_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-022 Monitoring and Measurement of Products",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1250164883",
            },
            currentProgress: "N/A",
            processPoint: "Control of Non-Conforming Products",
            plannedMeasurement: "Quality Monitoring",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: (
                <>
                    <p>Nonconforming Product Records</p>
                    <p>Compare “date discovered” against initiation date of document change request (DCR)</p>
                </>
            ),
            kpi: "Nonconforming product is addressed promptly, within designated timeframe",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: SCHEDULE_FOR_RECURRING_QMS_ACTIVITIES_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L3-WI-032 Schedule for recurring QMS activities",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1266417723",
            },
            currentProgress: "N/A",
            processPoint: "Schedule for Recurring QMS Activities",
            plannedMeasurement: "Completion Rate",
            reviewFrequency: "Quarterly",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Confirmation that task was completed.</>,
            kpi: "Achieve a 100% completion rate for recurring QMS activities according to the established schedule (with justification)",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
    },
    {
        reviewProcessPoint: DEVICE_MANAGEMENT_PROCESS_POINT,
        data: {
            relatedSOP: {
                name: "L2-SP-021 Control of Measuring and Monitoring Devices",
                link: "https://pascallqms.atlassian.net/wiki/spaces/QMS/pages/1251377179/L2-SP-021+Control+of+Measuring+and+Monitoring+Devices",
            },
            currentProgress: "N/A",
            processPoint: "Device Management",
            plannedMeasurement: "Timely completion of reviews",
            reviewFrequency: "Semi-annually",
            performedBy: "QA",
            performedIn: "Management Review",
            documentation: <>Compare the "Next Calibration Due Date" of the Operational Devices gainst the "Review date" of devices </>,
            kpi: "Achive a 100% of measurement devices are in calibration",
            improvementGoals: "N/A",
            trendingAnalysisMethodology: "Time series statistics",
        },
        pageUrl: APP_URLS.DEVICE_MANAGEMENT,
    },
];

const DEFAULT_PROGRESS = "N/A";
const DEFAULT_LOADING = false;
export const KPI_PROGRESS_MAP: Map<string, string> = new Map<string, string>([
    [SOP_REVIEW_PROCESS_POINT, DEFAULT_PROGRESS],
    [CHANGE_REQUEST_CLOSURE_PROCESS_POINT, DEFAULT_PROGRESS],
    [SUPPLIER_PERFORMANCE_PROCESS_POINT, DEFAULT_PROGRESS],
    [SUPPLIER_RE_EVALUATION_PROCESS_POINT, DEFAULT_PROGRESS],
    [PURCHASE_ACCEPTANCE_PROCESS_POINT, DEFAULT_PROGRESS],
    [RESIDUAL_RISK_PROCESS_POINT, DEFAULT_PROGRESS],
    [TRAINING_COMPETENCY_PROCESS_POINT, DEFAULT_PROGRESS],
    [TRAINING_MANAGEMENT_PROCESS_POINT, DEFAULT_PROGRESS],
    [CAPA_MANAGEMENT_PROCESS_POINT, DEFAULT_PROGRESS],
    [CAPA_EFFECTIVENESS_PROCESS_POINT, DEFAULT_PROGRESS],
    [NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT, DEFAULT_PROGRESS],
    [INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT, DEFAULT_PROGRESS],
    [MANAGEMENT_REVIEW_PROCESS_POINT, DEFAULT_PROGRESS],
    [NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT, DEFAULT_PROGRESS],
    [CONTROL_OF_NON_CONFORMING_PRODUCTS_PROCESS_POINT, DEFAULT_PROGRESS],
    [SCHEDULE_FOR_RECURRING_QMS_ACTIVITIES_PROCESS_POINT, DEFAULT_PROGRESS],
    [DEVICE_MANAGEMENT_PROCESS_POINT, DEFAULT_PROGRESS],
]);

export const KPI_LOADING_MAP: Map<string, boolean> = new Map<string, boolean>([
    [SOP_REVIEW_PROCESS_POINT, DEFAULT_LOADING],
    [CHANGE_REQUEST_CLOSURE_PROCESS_POINT, DEFAULT_LOADING],
    [SUPPLIER_PERFORMANCE_PROCESS_POINT, DEFAULT_LOADING],
    [SUPPLIER_RE_EVALUATION_PROCESS_POINT, DEFAULT_LOADING],
    [PURCHASE_ACCEPTANCE_PROCESS_POINT, DEFAULT_LOADING],
    [RESIDUAL_RISK_PROCESS_POINT, DEFAULT_LOADING],
    [TRAINING_COMPETENCY_PROCESS_POINT, DEFAULT_LOADING],
    [TRAINING_MANAGEMENT_PROCESS_POINT, DEFAULT_LOADING],
    [CAPA_MANAGEMENT_PROCESS_POINT, DEFAULT_LOADING],
    [CAPA_EFFECTIVENESS_PROCESS_POINT, DEFAULT_LOADING],
    [NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT, DEFAULT_LOADING],
    [INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT, DEFAULT_LOADING],
    [MANAGEMENT_REVIEW_PROCESS_POINT, DEFAULT_LOADING],
    [NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT, DEFAULT_LOADING],
    [CONTROL_OF_NON_CONFORMING_PRODUCTS_PROCESS_POINT, DEFAULT_LOADING],
    [SCHEDULE_FOR_RECURRING_QMS_ACTIVITIES_PROCESS_POINT, DEFAULT_LOADING],
    [DEVICE_MANAGEMENT_PROCESS_POINT, DEFAULT_LOADING],
]);

export const KPI_THRESHOLD = {
    [SOP_REVIEW_PROCESS_POINT]: 1,
    [PURCHASE_ACCEPTANCE_PROCESS_POINT]: 0.9,
    [TRAINING_COMPETENCY_PROCESS_POINT]: 1,
    [TRAINING_MANAGEMENT_PROCESS_POINT]: 0.9,
    [CAPA_MANAGEMENT_PROCESS_POINT]: 0.9,
    [NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT]: 1,
    [INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT]: 1,
    [MANAGEMENT_REVIEW_PROCESS_POINT]: 4,
    [NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT]: 0.8,
    [DEVICE_MANAGEMENT_PROCESS_POINT]: 1,
};

export enum KPI_STATUS {
    PASSED = 1,
    NOT_FOUND = 2,
    FAILED = 3,
}

export const KPI_STATUS_MAP: Map<string, number> = new Map<string, number>([
    [SOP_REVIEW_PROCESS_POINT, KPI_STATUS.PASSED],
    [CHANGE_REQUEST_CLOSURE_PROCESS_POINT, KPI_STATUS.PASSED],
    [SUPPLIER_PERFORMANCE_PROCESS_POINT, KPI_STATUS.PASSED],
    [SUPPLIER_RE_EVALUATION_PROCESS_POINT, KPI_STATUS.PASSED],
    [PURCHASE_ACCEPTANCE_PROCESS_POINT, KPI_STATUS.PASSED],
    [RESIDUAL_RISK_PROCESS_POINT, KPI_STATUS.PASSED],
    [TRAINING_COMPETENCY_PROCESS_POINT, KPI_STATUS.PASSED],
    [TRAINING_MANAGEMENT_PROCESS_POINT, KPI_STATUS.PASSED],
    [CAPA_MANAGEMENT_PROCESS_POINT, KPI_STATUS.PASSED],
    [CAPA_EFFECTIVENESS_PROCESS_POINT, KPI_STATUS.PASSED],
    [NONCONFORMANCE_AND_INVESTIGATION_PROCESS_POINT, KPI_STATUS.PASSED],
    [INTERNAL_AUDIT_FREQUENCY_PROCESS_POINT, KPI_STATUS.PASSED],
    [MANAGEMENT_REVIEW_PROCESS_POINT, KPI_STATUS.PASSED],
    [NUMBER_OF_CREATED_VS_RESOLVED_ISSUES_PROCESS_POINT, KPI_STATUS.PASSED],
    [CONTROL_OF_NON_CONFORMING_PRODUCTS_PROCESS_POINT, KPI_STATUS.PASSED],
    [SCHEDULE_FOR_RECURRING_QMS_ACTIVITIES_PROCESS_POINT, KPI_STATUS.PASSED],
    [DEVICE_MANAGEMENT_PROCESS_POINT, KPI_STATUS.PASSED],
]);
